import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { DisableUnsupportedSyntaxPlugin, UnderlinePlugin } from './plugins';
import { Box, BoxProps, Flex, Image, Link, ListItem, OrderedList, Text, UnorderedList } from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { FC, useMemo } from 'react';

// side-effect import so nextjs builds using transpilePackages pick up the type augmentation
import './mdast';

const components: Components = {
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  u: ({ children }) => <u>{children}</u>,
  p: ({ children }) => <Text mb={4}>{children}</Text>,
  ul: ({ children }) => <UnorderedList mb={4}>{children}</UnorderedList>,
  li: ({ children }) => <ListItem>{children}</ListItem>,
  ol: ({ children }) => <OrderedList mb={4}>{children}</OrderedList>,
  img: ({ src, alt }) => <Image src={src} alt={alt} mb={4} />,
  a: ({ href, rel, target, children }) => {
    rel ||= 'nofollow noopener noreferrer';
    target ||= '_blank';
    return (
      <Link href={href} rel={rel} target={target} color="primary" textDecoration="underline">
        {target === '_blank' ? (
          <Flex as="span" alignItems="center">
            {children} <ExternalLinkIcon ml={1} />
          </Flex>
        ) : (
          children
        )}
      </Link>
    );
  },
};

export type MarkdownRendererProps = Omit<BoxProps, 'content'> & {
  content: string;
  /**
   * Limits the supported syntax to the elements the HtmlEditor can create, so that markdown
   * characters the editor keeps as plain text are not rendered as formatting here either.
   * Enable it for user generated content, leave it off to render complete markdown.
   */
  restrictSyntax?: boolean;
};

export const MarkdownRenderer: FC<MarkdownRendererProps> = ({ content, restrictSyntax, ...rest }) => {
  const remarkPlugins = useMemo(
    () => (restrictSyntax ? [DisableUnsupportedSyntaxPlugin, UnderlinePlugin] : [UnderlinePlugin]),
    [restrictSyntax]
  );

  return (
    <Box {...rest}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};
